import React, { createContext, useContext, ReactNode } from 'react'

import { Icons } from '@/components/icons'
import { env } from '@/configs/env'
import { useAuthHook } from '@/hooks'

interface WhatsAppContextType {
    openWhatsAppSupport: () => void
}

const WhatsAppContext = createContext<WhatsAppContextType | undefined>(
    undefined
)

export const useWhatsApp = (): WhatsAppContextType => {
    const context = useContext(WhatsAppContext)

    if (!context) {
        throw new Error('useWhatsApp must be used within a WhatsAppProvider')
    }

    return context
}

export const WhatsAppProvider: React.FC<{ children: ReactNode }> = ({
    children
}) => {
    const { firstName, lastName, email, cellphone, idAspirant, isAgent } =
        useAuthHook()
    const whatsappNumber = env.supportWhatsAppNumber || '573195650616'

    const openWhatsAppSupport = () => {
        // Format user data with more complete information
        const name =
            firstName && lastName
                ? `${firstName} ${lastName}`.trim()
                : 'Usuario'
        const userEmail = email || ''
        const phone = cellphone || ''
        const documentId = idAspirant ? idAspirant.toString() : ''

        // Create message with emojis and improved language
        const message = `¡Hola! Mi nombre es ${name}. \n\n Correo: ${userEmail} \n Teléfono: ${phone} \n ID Aspirante: ${documentId} \n\n Necesito asistencia con el proceso de inscripción en el Journey del Aspirante.`

        // Encode the message for URL
        const encodedMessage = encodeURIComponent(message)

        // Open WhatsApp in a new tab
        window.open(
            `https://wa.me/${whatsappNumber}?text=${encodedMessage}`,
            '_blank'
        )
    }

    return (
        <WhatsAppContext.Provider value={{ openWhatsAppSupport }}>
            {children}
            {!isAgent && <WhatsAppFloatingButton />}
        </WhatsAppContext.Provider>
    )
}

const WhatsAppFloatingButton: React.FC = () => {
    const { openWhatsAppSupport } = useWhatsApp()

    return (
        <>
            {/* Desktop version - right corner */}
            <div className="whatsapp-fab fixed bottom-8 right-8 z-50 hidden md:block">
                <button
                    onClick={() => openWhatsAppSupport()}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-transform hover:scale-110 focus:outline-none flex items-center justify-center"
                    aria-label="Contactar soporte por WhatsApp"
                    title="¿Necesitas ayuda? Contáctenos por WhatsApp"
                >
                    <Icons.Whatsapp className="w-8 h-8" />
                </button>
            </div>

            {/* Mobile version - sticky at bottom */}
            <div className="whatsapp-fab fixed bottom-4 right-4 z-50 md:hidden">
                <button
                    onClick={() => openWhatsAppSupport()}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full p-3 shadow-lg transition-transform hover:scale-110 focus:outline-none flex items-center justify-center"
                    aria-label="Contactar soporte por WhatsApp"
                >
                    <Icons.Whatsapp className="w-6 h-6" />
                </button>
            </div>
        </>
    )
}
